import { ApiService } from '@shared/services/api.service';
import { apiErrorBody, apiErrorMessage, apiErrorStatus } from './auditorium-api-error';
import {
  AuditoriumFloorSessions,
  AuditoriumJoinInfo,
  AuditoriumSchedule,
  ScheduleAuditoriumInput,
} from '../types';

/**
 * Auditorium scheduling API: booking an idle auditorium, the floor's session list,
 * starting a booking, and resolving a shared join link.
 *
 * Times cross the wire as UTC ISO strings — the pickers work in the viewer's zone.
 */

/**
 * Where a shared link leaves its join token for the join flow to pick up. An
 * invite-only session requires it on the guest's token request — the guest arrives
 * with nothing else that proves they were invited.
 */
export const AUDITORIUM_JOIN_TOKEN_KEY = 'auditoriumJoinToken';

/** The join token from the link that brought this visitor here, if any. */
export const readStoredJoinToken = (): string | null => {
  try {
    return sessionStorage.getItem(AUDITORIUM_JOIN_TOKEN_KEY);
  } catch {
    return null;
  }
};

/** A 409 from scheduling or starting, with the fields the backend attaches to it. */
export interface AuditoriumScheduleConflict {
  message: string;
  /** Set when a session is already live in the room. */
  live_session_pid?: string;
  /** Set when the requested window overlaps another booking. */
  conflicting_session_pid?: string;
  /** UTC ISO start of the session that was clashed with. */
  scheduled_start_at?: string | null;
  /**
   * UTC ISO end of that session. A booking with no end of its own reports the implied
   * one the overlap was measured against, so the window is always a range.
   */
  scheduled_end_at?: string | null;
}

/**
 * The error body for a rejected booking, or null for anything else. The API's error
 * filter keeps the thrower's extra fields alongside `message`, so the conflicting
 * session's time comes back with it and the form can name the clash.
 */
export const getScheduleConflict = (error: unknown): AuditoriumScheduleConflict | null => {
  if (apiErrorStatus(error) !== 409) return null;
  const body = apiErrorBody(error);
  return {
    message: apiErrorMessage(error) ?? 'This auditorium is busy',
    live_session_pid: body.live_session_pid as string | undefined,
    conflicting_session_pid: body.conflicting_session_pid as string | undefined,
    scheduled_start_at: (body.scheduled_start_at as string | null | undefined) ?? null,
    scheduled_end_at: (body.scheduled_end_at as string | null | undefined) ?? null,
  };
};

/** A refusal the API explained (past start time, bad window, not an auditorium). */
export const getRequestErrorMessage = (error: unknown): string | null => apiErrorMessage(error);

/** Book a session in an idle auditorium. The caller becomes host and moderator. */
export const scheduleAuditoriumSession = (
  input: ScheduleAuditoriumInput
): Promise<AuditoriumSchedule> =>
  ApiService.request<AuditoriumSchedule>({
    endpoint: 'auditorium/schedule',
    method: 'POST',
    data: input,
  });

/**
 * Sessions booked on a floor. `live_sessions` maps room pid → session pid, which is
 * what says whether an auditorium is free to book or already busy.
 */
export const listFloorAuditoriumSessions = (
  floorPid: string,
  status: 'scheduled' | 'live' | 'all' = 'all'
): Promise<AuditoriumFloorSessions> =>
  ApiService.request<AuditoriumFloorSessions>({
    endpoint: `auditorium/floor/${floorPid}/sessions`,
    method: 'GET',
    data: { status },
  });

/** Flip a booking live (host/moderator only). */
export const startAuditoriumSession = (sessionPid: string): Promise<AuditoriumSchedule> =>
  ApiService.request<AuditoriumSchedule>({
    endpoint: `auditorium/${sessionPid}/start`,
    method: 'POST',
  });

/**
 * End a session that is running. Every session live in that auditorium is closed
 * (host/moderator only); the room's other bookings are left alone for their own
 * hosts to start later.
 */
export const endAuditoriumSession = (sessionPid: string): Promise<{ ended: number }> =>
  ApiService.request<{ ended: number }>({
    endpoint: `auditorium/${sessionPid}/end`,
    method: 'POST',
  });

/**
 * Lock or unlock a live session's chat (host/moderator only — the same people who may
 * end it). The API answers with the state it settled on, which is what the caller should
 * show rather than what it asked for.
 */
export const setAuditoriumChatLock = (
  sessionPid: string,
  chatLocked: boolean
): Promise<{ chat_locked: boolean }> =>
  ApiService.request<{ chat_locked: boolean }>({
    endpoint: `auditorium/${sessionPid}/chat/lock`,
    method: 'POST',
    data: { chat_locked: chatLocked },
  });

/**
 * Call off a booking that hasn't started (host/moderator only). The join token stays
 * resolvable on purpose, so anyone holding the link is told the session is off
 * instead of meeting a dead page. A live session is ended, not cancelled — the API
 * refuses this for one.
 */
export const cancelAuditoriumSession = (sessionPid: string): Promise<AuditoriumSchedule> =>
  ApiService.request<AuditoriumSchedule>({
    endpoint: `auditorium/${sessionPid}/cancel`,
    method: 'POST',
  });

/**
 * Resolve a shared join link. Public — no auth, no guest token — so the landing page
 * can show the session before the visitor is anyone. Ended sessions still resolve;
 * an unknown token 404s.
 */
export const resolveAuditoriumJoinToken = (
  token: string,
  invite?: string | null
): Promise<AuditoriumJoinInfo> =>
  ApiService.request<AuditoriumJoinInfo>({
    endpoint: invite
      ? `auditorium/join/${token}?invite=${encodeURIComponent(invite)}`
      : `auditorium/join/${token}`,
    method: 'GET',
  });

/**
 * Public session info by pid — the same lookup the guest form uses. Public, so a guest
 * waiting for the session to start can poll it without a join token.
 */
export const fetchAuditoriumPublicInfo = (sessionPid: string): Promise<AuditoriumJoinInfo> =>
  ApiService.request<AuditoriumJoinInfo>({
    endpoint: `auditorium/${sessionPid}/public`,
    method: 'GET',
  });
